import _, {Dictionary} from 'lodash';
import React, {FunctionComponent, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useHistory} from 'react-router-dom';
import {Collapse} from '@material-ui/core';
import Button, {ButtonClass} from '@treez-ui/core/buttons/Button';
import {SelectInput} from '@treez-ui/core/forms';
import AdvancedProductFilter from '@treez-ui/core/modules/AdvancedProductFilter';
import {ProductTypePicker} from '@treez-ui/core/pickers';
import {
    CatalogProductStatus,
    ProductField,
    ProductType,
} from '@treez/commons/sharedTypings/product';
import {
    generateQueryString,
    getProductQueryParameters,
    getSearchQueryParameters,
    parseQueryString,
} from '~/lib/routing';
import SearchBar from '~/modules/forms/SearchBar';
import FiltersButton from '~/modules/widgets/FiltersButton';
import {selectProductMetaData} from '~/redux/selectors/ProductMetaDataSelectors';
import {translateProductParamsToProductFilters} from '~/lib/product';
import StoresToggle from './StoresToggle';

const {TYPE} = ProductField;

export const ProductOptions: FunctionComponent = () => {
    const history = useHistory();
    const location = useLocation();
    const pageParameters = parseQueryString(location.search);

    const {searchTerm} = pageParameters;

    const hideStores = pageParameters.hideStores === 'true';

    const productParams = getProductQueryParameters(pageParameters);

    const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(false);

    const productMetaData = useSelector(selectProductMetaData, _.isEqual);

    const {
        brandOptions,
        effectAttributesOptions,
        flavorAttributesOptions,
        generalAttributesOptions,
        ingredientAttributesOptions,
        internalAttributesOptions,
    } = productMetaData;

    const handleUpdateSearchParamWrapper = (parameters) => {
        history.replace({
            search: generateQueryString({
                ...parameters,
                page: 1,
            }),
        });
    };

    const handleApplyAdvancedFilters = (filtersSelected: Dictionary<string[]>) => {
        const {
            brands,
            subtypes,
            classifications,
            internalTags,
            flavors,
            effects,
            ingredients,
            generalAttributes,
        } = filtersSelected;

        const newParameters = _.omitBy(
            {
                ...pageParameters,
                ...{
                    brand: brands,
                    subtype: subtypes,
                    classification: classifications,
                    internalTag: internalTags,
                    flavor: flavors,
                    effect: effects,
                    ingredient: ingredients,
                    generalAttribute: generalAttributes,
                },
            },
            (values) => {
                return _.isEmpty(values) || values == null;
            }
        );

        handleUpdateSearchParamWrapper(newParameters);
    };

    const clearFilters = () => {
        const filterlessParams = _.omit(getSearchQueryParameters(pageParameters), 'searchTerm');

        handleUpdateSearchParamWrapper(filterlessParams);
    };

    const handleProductTypeSelect = (productTypesSelected: ProductType[]) => {
        const newPageParameters = pageParameters;

        if (_.isEmpty(productTypesSelected)) {
            delete newPageParameters.type;
        } else {
            newPageParameters.type = productTypesSelected;
        }

        handleUpdateSearchParamWrapper(newPageParameters);
    };

    const toggleHideStores = () => {
        handleUpdateSearchParamWrapper({
            ...parseQueryString(location.search),
            hideStores: !hideStores,
        });
    };

    const setSearchTerm = (searchedTerm: string | undefined) => {
        if (searchedTerm === searchTerm) {
        } else {
            handleUpdateSearchParamWrapper({
                ...pageParameters,
                searchTerm: searchedTerm,
            });
        }
    };

    const updateStatusFilter = (productStatus: CatalogProductStatus) => {
        handleUpdateSearchParamWrapper({
            ...pageParameters,
            status: productStatus,
        });
    };

    const clearStatusFilter = () => {
        handleUpdateSearchParamWrapper(_.omit(pageParameters, 'status'));
    };

    const productFiltersApplied = translateProductParamsToProductFilters(productParams);

    return (
        <div className="product-options-container">
            <div className="options-row">
                <div className="search-and-filter-container">
                    <FiltersButton
                        advancedFiltersOpen={filterMenuOpen}
                        filtersApplied={_.flatten(_.values(productParams)).length > 0}
                        toggleFilterMenuOpen={() => setFilterMenuOpen(!filterMenuOpen)}
                    />
                    <SearchBar
                        searchTerm={searchTerm as string | undefined}
                        setSearchTerm={setSearchTerm}
                    />
                </div>
                <div>
                    <StoresToggle storesHidden={hideStores} toggleStoreView={toggleHideStores} />
                </div>
            </div>
            <div className="options-row">
                <div className="status-and-type-filters">
                    <div className="product-status-select">
                        <SelectInput
                            options={[
                                CatalogProductStatus.ACTIVE,
                                CatalogProductStatus.DEACTIVATED,
                            ]}
                            onClear={clearStatusFilter}
                            onChange={updateStatusFilter}
                            value={
                                pageParameters.status
                                    ? (pageParameters.status as string)
                                    : CatalogProductStatus.ACTIVE
                            }
                            placeholder="Product Status"
                        />
                    </div>
                    <ProductTypePicker
                        selected={_.get(productParams, TYPE, []) as ProductType[]}
                        setSelected={handleProductTypeSelect}
                    />
                </div>

                <Button
                    text="Clear Filters"
                    buttonClass={ButtonClass.QUIET}
                    onClick={clearFilters}
                />
            </div>
            <Collapse in={filterMenuOpen}>
                <div className="advanced-filter-container">
                    <AdvancedProductFilter
                        brands={brandOptions}
                        effects={effectAttributesOptions}
                        ingredients={ingredientAttributesOptions}
                        generalAttributes={generalAttributesOptions}
                        filters={productFiltersApplied as Dictionary<string[] | undefined>}
                        internalTags={internalAttributesOptions}
                        onChange={handleApplyAdvancedFilters}
                        flavors={flavorAttributesOptions}
                    />
                </div>
            </Collapse>
        </div>
    );
};

export default ProductOptions;
